import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

/**
 * Format a date string or timestamp to a readable format
 */
export const formatDate = (
  date: string | Date | null | undefined,
  format: string = 'MMM D, YYYY'
): string => {
  if (!date) return '-';
  return dayjs(date).format(format);
};

/**
 * Format a date to a relative time string (e.g., "2 hours ago")
 */
export const formatRelativeTime = (date: string | Date | null | undefined): string => {
  if (!date) return '-';
  return dayjs(date).fromNow();
};

/**
 * Format a date with time
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  format: string = 'MMM D, YYYY h:mm A'
): string => {
  if (!date) return '-';
  return dayjs(date).format(format);
};

/**
 * Format currency amount
 */
export const formatCurrency = (
  amount: number | null | undefined,
  currency: string = 'USD',
  minimumFractionDigits: number = 0
): string => {
  if (amount === null || amount === undefined) return '-';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format a number with thousand separators
 */
export const formatNumber = (
  value: number | null | undefined,
  maximumFractionDigits: number = 0
): string => {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
  }).format(value);
};

/**
 * Format a percentage value
 */
export const formatPercentage = (
  value: number | null | undefined,
  fractionDigits: number = 1
): string => {
  if (value === null || value === undefined) return '-';

  return `${value.toFixed(fractionDigits)}%`;
};

/**
 * Get a display-friendly subscription tier name
 */
export const formatSubscriptionTier = (planId: string): string => {
  const tierNames: Record<string, string> = {
    free: 'Free',
    pro_monthly: 'Pro (Monthly)',
    pro_annual: 'Pro (Annual)',
    elite_monthly: 'Elite (Monthly)',
    elite_annual: 'Elite (Annual)',
  };

  return tierNames[planId] || planId;
};

/**
 * Get a display-friendly subscription status
 */
export const formatSubscriptionStatus = (status: string): string => {
  const statusNames: Record<string, string> = {
    active: 'Active',
    expired: 'Expired',
    inactive: 'Inactive',
    cancelled: 'Cancelled',
    past_due: 'Past Due',
  };

  return statusNames[status] || status;
};

/**
 * Truncate a string to a maximum length
 */
export const truncate = (str: string, maxLength: number = 30): string => {
  if (!str) return '';
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}...`;
};
